// ==========================================================================
// ATTENDANCE & TIMESHEET MANAGEMENT MODULE
// WuRuPayroll - Monthly attendance grid, daily records, locks & calculations
// ==========================================================================

import {
  getTimesheet,
  saveTimesheet,
  updateEmployee
} from '../firebase-db.js';

import { escapeHtml, escapeAttr, escapeJsParam } from '../utils/sanitize.js';
import { toast, showConfirm } from '../utils/toast.js';
import {
  calculatePayrollForEmployee,
  finalizeTimesheetDays,
  isEmployeeActiveInMonth,
  getEmployeeDeductionSettings,
  getEmployeeWorkHoursStr,
  parseWorkHoursRange,
  calculateMaxAdvanceAllowed
} from './payroll-calc.js';
import { addTimesheetAdjustmentRow } from './employees.js';

// Safe proxy for global application state
const state = new Proxy({}, {
  get(target, prop) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    return s[prop];
  },
  set(target, prop, value) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    s[prop] = value;
    return true;
  }
});

// Safe wrappers for app routing, context and permissions
const navigateTo = (page) => {
  if (typeof window !== 'undefined' && window.navigateTo) {
    return window.navigateTo(page);
  }
};

const checkPermission = (action) => {
  if (typeof window !== 'undefined' && window.checkPermission) {
    return window.checkPermission(action);
  }
  return true;
};

const canViewSalary = (emp) => {
  if (typeof window !== 'undefined' && window.canViewSalary) {
    return window.canViewSalary(emp);
  }
  return true;
};

// ==========================================================================
// ATTENDANCE RECORDING PAGE (Requirement 11, 12, 26)
// ==========================================================================
function renderAttendancePage() {
  document.getElementById('page-display-title').textContent = 'บันทึกเวลาทำงานรายเดือน';
  document.getElementById('page-display-desc').textContent = `บันทึกการ ขาด ลา มาสาย และการเบิกล่วงหน้ารายบุคคลสำหรับเดือน ${state.selectedMonth}`;

  const wrapper = document.getElementById('page-content-wrapper');

  let employeesListHTML = '';
  const activeEmps = state.employees.filter(e => isEmployeeActiveInMonth(e, state.selectedMonth));

  if (activeEmps.length === 0) {
    wrapper.innerHTML = `
      <div class="glass-card text-center" style="padding: 50px;">
        <h4 style="color: var(--text-sub);">ไม่พบข้อมูลพนักงานในระบบของบริษัทนี้</h4>
      </div>
    `;
    return;
  }

  activeEmps.forEach(emp => {
    const typeLabel = emp.type === 'salary' ? 'เงินเดือนประจำ' : (emp.type === 'daily' ? 'รายวัน' : 'ฟรีแลนซ์');
    employeesListHTML += `
      <div class="glass-card" style="margin-bottom: 15px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 15px;">
        <div>
          <h4 style="font-weight: 700; color: var(--primary-dark);">${escapeHtml(emp.realName)} (${escapeHtml(emp.nickname)})</h4>
          <span style="font-size: 0.85rem; color: var(--text-sub);">ประเภทการจ้าง: ${typeLabel}</span>
        </div>
        <div>
          <button class="btn btn-primary" onclick="openEmployeeTimesheetPage('${escapeJsParam(emp.id)}')">📝 กรอกตารางเวลา 1-31 วัน</button>
        </div>
      </div>
    `;
  });

  wrapper.innerHTML = `
    <div style="display: flex; flex-direction: column; gap: 15px;">
      ${employeesListHTML}
    </div>
  `;
}

// Open timesheet interactive grid 1-31 days for specific employee
function openEmployeeTimesheetPage (empId) {
  state.activeEditingEmployeeId = empId;
  state.timesheetReturnPage = (state.activePage && state.activePage !== 'timesheet') ? state.activePage : 'dashboard';
  navigateTo('timesheet');
};

function goBackFromTimesheet () {
  const returnPage = (state.timesheetReturnPage && state.timesheetReturnPage !== 'timesheet') ? state.timesheetReturnPage : 'dashboard';
  navigateTo(returnPage);
};

async function initRetroactiveTimesheet (empId, month) {
  // Save the phantom timesheet defaults to the database to officially create it
  try {
    const emp = state.employees.find(e => e.id === empId);
    const ts = await getTimesheet(empId, month);

    // Auto-detect default status for convenience (reduces manual editing)
    const [yearStr, monthStr] = month.split('-');
    const year = parseInt(yearStr);
    const m = parseInt(monthStr);
    const daysInMonth = new Date(year, m, 0).getDate();

    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj = new Date(year, m - 1, d);
      const dateString = `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = dayOfWeekNames[dateObj.getDay()];

      let detectedStatus = 'work';

      let isDefaultDayOff = false;
      if (emp.weeklyDaysOff !== undefined && emp.weeklyDaysOff.length > 0) {
        isDefaultDayOff = emp.weeklyDaysOff.includes(dateObj.getDay());
      } else {
        const workHoursStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, dayName);
        isDefaultDayOff = workHoursStr === '0' || workHoursStr.startsWith('OFF:');
      }

      const isCompanyHoliday = state.holidays && state.holidays.some(h => h.date === dateString);
      const isSpecialWorkday = state.specialWorkdays && state.specialWorkdays.some(s => s.date === dateString);
      const isCompensatoryHoliday = state.specialWorkdays && state.specialWorkdays.some(s => s.compensatoryOffDate === dateString);

      const now = new Date();
      const todayString = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      if (emp.startDate && dateString < emp.startDate) detectedStatus = 'not_started';
      else if (emp.resignDate && dateString > emp.resignDate) detectedStatus = 'resigned';
      else if (dateString > todayString) detectedStatus = 'not_started';
      else if (isSpecialWorkday) detectedStatus = 'special_workday';
      else if (isCompensatoryHoliday) detectedStatus = 'compensatory_holiday';
      else if (isCompanyHoliday) detectedStatus = 'holiday';
      else if (isDefaultDayOff) detectedStatus = 'day_off';

      if (ts.days && ts.days[d]) {
        // Only override if the day is untouched ('work') or if it strictly falls outside employment periods
        if (ts.days[d].status === 'work' || detectedStatus === 'not_started' || detectedStatus === 'resigned') {
          ts.days[d].status = detectedStatus;
        }
      }
    }

    ts.isPhantom = false;
    await saveTimesheet(empId, month, ts);
    renderTimesheetPage();
  } catch (err) {
    console.error("Error creating retroactive timesheet:", err);
    alert("เกิดข้อผิดพลาดในการสร้างข้อมูลย้อนหลัง");
  }
};

async function renderTimesheetPage () {
  const empId = state.activeEditingEmployeeId;
  const emp = state.employees.find(e => e.id === empId);
  const ts = await getTimesheet(empId, state.selectedMonth);
  const canView = window.canViewSalary(emp);

  const [yearStr, monthStr] = state.selectedMonth.split('-');
  const year = parseInt(yearStr);
  const month = parseInt(monthStr);
  const daysInMonth = new Date(year, month, 0).getDate();

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const monthDiff = (currentYear - year) * 12 + (currentMonth - month);
  const isPastMonth = monthDiff > 0;
  const isFutureMonth = monthDiff < 0;

  let canViewThisMonth = window.checkPermission('view_timesheet');
  let canEditThisMonth = window.checkPermission('edit_timesheet');

  if (isPastMonth) {
    const viewLimit = window.checkPermission('view_past_months_limit') ?? 0;
    if (monthDiff > viewLimit) canViewThisMonth = false;

    const editLimit = window.checkPermission('edit_past_months_limit') ?? 0;
    if (monthDiff > editLimit) canEditThisMonth = false;
  }

  if (isFutureMonth) {
    const futureDiff = Math.abs(monthDiff);
    const viewLimit = window.checkPermission('view_future_months_limit') ?? 0;
    if (futureDiff > viewLimit) canViewThisMonth = false;

    const editLimit = window.checkPermission('edit_future_months_limit') ?? 0;
    if (futureDiff > editLimit) canEditThisMonth = false;
  }

  document.getElementById('page-display-title').textContent = 'บันทึกเวลาทำงานรายวัน';
  document.getElementById('page-display-desc').textContent = `ตารางเวลาทำงานสำหรับพนักงาน ประจำเดือน ${state.selectedMonth}`;

  const wrapper = document.getElementById('page-content-wrapper');

  if (!canViewThisMonth) {
    wrapper.innerHTML = `
      <div style="max-width: 800px; margin: 50px auto; text-align: center; background: var(--white); padding: 40px; border-radius: var(--border-radius-lg); box-shadow: var(--shadow);">
        <h2 style="color: var(--primary-dark); margin-bottom: 20px;">🔒 ไม่มีสิทธิ์เข้าถึงข้อมูล</h2>
        <p style="color: var(--text-sub); margin-bottom: 30px; font-size: 1.1rem;">
          คุณไม่ได้รับสิทธิ์ให้ดูข้อมูล${isPastMonth ? 'ย้อนหลัง' : 'ล่วงหน้า'}ในเดือนนี้
        </p>
      </div>
    `;
    return;
  }

  if (ts.isPhantom && isPastMonth) {
    wrapper.innerHTML = `
      <div style="max-width: 800px; margin: 50px auto; text-align: center; background: var(--white); padding: 40px; border-radius: var(--border-radius-lg); box-shadow: var(--shadow);">
        <h2 style="color: var(--primary-dark); margin-bottom: 20px;">📅 ยังไม่มีข้อมูลของเดือนนี้</h2>
        <p style="color: var(--text-sub); margin-bottom: 30px; font-size: 1.1rem;">
          เดือน <strong>${state.selectedMonth}</strong> เป็นเดือนในอดีตที่ยังไม่มีการบันทึกข้อมูลใดๆ ในระบบ<br>
          หากคุณต้องการบันทึกข้อมูลย้อนหลัง กรุณากดปุ่มด้านล่างเพื่อเริ่มสร้างตารางลงเวลา
        </p>
        <button class="btn btn-primary" style="font-size: 1.1rem; padding: 12px 24px;" onclick="initRetroactiveTimesheet('${empId}', '${state.selectedMonth}')">
          ✨ เริ่มสร้างข้อมูลย้อนหลัง
        </button>
      </div>
    `;
    return;
  }

  if (ts.isPhantom && isFutureMonth) {
    wrapper.innerHTML = `
      <div style="max-width: 800px; margin: 50px auto; text-align: center; background: var(--white); padding: 40px; border-radius: var(--border-radius-lg); box-shadow: var(--shadow);">
        <h2 style="color: var(--primary-dark); margin-bottom: 20px;">🔮 ยังไม่ถึงรอบการบันทึกข้อมูล</h2>
        <p style="color: var(--text-sub); margin-bottom: 30px; font-size: 1.1rem;">
          เดือน <strong>${state.selectedMonth}</strong> เป็นเดือนในอนาคตที่ยังไม่ถึงกำหนดเวลา<br>
          หากคุณต้องการสร้างตารางลงเวลาล่วงหน้า กรุณากดปุ่มด้านล่าง (แนะนำให้ทำเฉพาะเมื่อจำเป็นเท่านั้น เพื่อป้องกันการสับสน)
        </p>
        <button class="btn btn-secondary" style="font-size: 1.1rem; padding: 12px 24px;" onclick="initRetroactiveTimesheet('${empId}', '${state.selectedMonth}')">
          ✨ เริ่มสร้างข้อมูลล่วงหน้า
        </button>
      </div>
    `;
    return;
  }

  // Pre-calculate payslip dynamically
  const summ = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);

  let adjustmentsHTML = '';
  if (!canView) {
    adjustmentsHTML = '<p style="color: var(--text-sub); margin-top: 10px;">**** (ไม่สามารถดูข้อมูลได้)</p>';
  } else if (emp.recurringItems) {
    emp.recurringItems.forEach((item, idx) => {
      if (item.name === 'เบี้ยขยัน') return;
      adjustmentsHTML += `
        <div class="adj-item-row" style="margin-bottom: 12px; padding: 10px; border: 1px solid var(--gray-300); border-radius: 6px; background: #fcfcfc; display: flex; flex-direction: column; gap: 8px;">
          <div style="display: flex; gap: 8px;">
            <input type="text" placeholder="รายการ" class="adj-name" value="${escapeAttr(item.name)}" required style="padding: 8px; flex: 2; font-size: 0.9rem;" ${ts.isLocked || !window.checkPermission('edit_payroll_adjustments') ? 'disabled' : ''}>
            <select class="adj-type" style="padding: 8px; flex: 1; font-size: 0.9rem;" ${ts.isLocked || !window.checkPermission('edit_payroll_adjustments') ? 'disabled' : ''}>
              <option value="income" ${item.type === 'income' ? 'selected' : ''}>รายรับ</option>
              <option value="deduction" ${item.type === 'deduction' ? 'selected' : ''}>รายหัก</option>
            </select>
          </div>
          <div style="display: flex; gap: 8px; align-items: center;">
            <input type="number" placeholder="จำนวน" class="adj-amount" value="${item.amount}" required style="padding: 8px; flex: 1; font-size: 0.9rem;" ${ts.isLocked || !window.checkPermission('edit_payroll_adjustments') ? 'disabled' : ''}>
            <select class="adj-recurrence" style="padding: 8px; flex: 2; font-size: 0.85rem; background: var(--warning-light);" ${ts.isLocked || !window.checkPermission('edit_payroll_adjustments') ? 'disabled' : ''}>
              <option value="recurring" selected>🔄 ประจำทุกเดือน</option>
              <option value="onetime">📌 เฉพาะเดือนนี้</option>
            </select>
            ${!ts.isLocked && window.checkPermission('edit_payroll_adjustments') ? `<button type="button" class="btn btn-danger btn-sm" onclick="this.closest('.adj-item-row').remove()" style="padding: 8px; font-weight: bold;">❌</button>` : ''}
          </div>
        </div>
      `;
    });
  }
  if (canView && ts.manualItems) {
    ts.manualItems.forEach((item, idx) => {
      adjustmentsHTML += `
        <div class="adj-item-row" style="margin-bottom: 12px; padding: 10px; border: 1px solid var(--gray-300); border-radius: 6px; background: #fcfcfc; display: flex; flex-direction: column; gap: 8px;">
          <div style="display: flex; gap: 8px;">
            <input type="text" placeholder="รายการ" class="adj-name" value="${escapeAttr(item.name)}" required style="padding: 8px; flex: 2; font-size: 0.9rem;" ${ts.isLocked || !window.checkPermission('edit_payroll_adjustments') ? 'disabled' : ''}>
            <select class="adj-type" style="padding: 8px; flex: 1; font-size: 0.9rem;" ${ts.isLocked || !window.checkPermission('edit_payroll_adjustments') ? 'disabled' : ''}>
              <option value="income" ${item.type === 'income' ? 'selected' : ''}>รายรับ</option>
              <option value="deduction" ${item.type === 'deduction' ? 'selected' : ''}>รายหัก</option>
            </select>
          </div>
          <div style="display: flex; gap: 8px; align-items: center;">
            <input type="number" placeholder="จำนวน" class="adj-amount" value="${item.amount}" required style="padding: 8px; flex: 1; font-size: 0.9rem;" ${ts.isLocked || !window.checkPermission('edit_payroll_adjustments') ? 'disabled' : ''}>
            <select class="adj-recurrence" style="padding: 8px; flex: 2; font-size: 0.85rem; background: var(--info-light);" ${ts.isLocked || !window.checkPermission('edit_payroll_adjustments') ? 'disabled' : ''}>
              <option value="recurring">🔄 ประจำทุกเดือน</option>
              <option value="onetime" selected>📌 เฉพาะเดือนนี้</option>
            </select>
            ${!ts.isLocked && window.checkPermission('edit_payroll_adjustments') ? `<button type="button" class="btn btn-danger btn-sm" onclick="this.closest('.adj-item-row').remove()" style="padding: 8px; font-weight: bold;">❌</button>` : ''}
          </div>
        </div>
      `;
    });
  }

  let gridRowsHTML = '';
  const effectiveSalaryRate = ts.overrides && ts.overrides.salaryRate !== undefined ? parseFloat(ts.overrides.salaryRate) : (emp.salaryRate || 0);

  // Status Translation Map for Selection Dropdown
  const statusOptions = [
    { value: 'work', label: '✅ มาทำงาน' },
    { value: 'absence', label: '❌ ขาดงาน' },
    { value: 'leave_personal', label: '💼 ลา' }
  ];

  // Only enable Sick Leave & Annual Leave options if they are enabled in system settings (Requirement 27)
  if (state.activeCompanySettings.enableSickLeaveCalc) {
    statusOptions.push({ value: 'leave_sick', label: '🤒 ลาป่วย' });
  }
  if (state.activeCompanySettings.enableAnnualLeaveCalc) {
    statusOptions.push({ value: 'leave_annual', label: '🌴 ลาพักร้อน' });
  }

  statusOptions.push(
    { value: 'holiday_selected', label: '💜 วันหยุดประจำปี (เลือกเอง)' },
    { value: 'holiday', label: '🚩 วันหยุดประจำปีบริษัท' },
    { value: 'compensatory_holiday', label: '💤 วันหยุดชดเชย' },
    { value: 'special_workday', label: '🛠️ วันทำงานพิเศษ' },
    { value: 'day_off', label: '💤 วันหยุดประจำสัปดาห์' },
    { value: 'not_started', label: '⏳ ยังไม่ถึงวัน / ยังไม่เริ่มงาน' },
    { value: 'resigned', label: '🚪 ลาออกแล้ว' }
  );

  // Auto-freeze warning if locked
  const lockWarningHTML = ts.isLocked ? `
    <div style="background: rgba(46,196,182,0.1); border: 1px solid var(--success); padding: 12px; border-radius: var(--border-radius-sm); color: var(--success); font-weight:700; display:flex; align-items:center; gap: 8px; margin-bottom: 15px;">
      🔒 งวดการคำนวณเดือนนี้ได้รับการยืนยันจ่ายเงินและทำการล็อกข้อมูลแล้ว! การเปลี่ยนค่าจะไม่มีผลจนกว่าจะมีการปลดล็อกโดยผู้มีสิทธิ์
    </div>
  ` : '';

  for (let d = 1; d <= daysInMonth; d++) {
    const dayData = ts.days[d] || { status: 'work', lateMinutes: 0, earlyLeaveMinutes: 0, advance: 0 };
    const dateObj = new Date(year, month - 1, d);
    const dateString = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

    // Check if date corresponds to a weekly day off (checks employee-specific first, then falls back to company settings switches)
    const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayOfWeekNames[dateObj.getDay()];

    let isDefaultDayOff = false;
    if (emp.weeklyDaysOff !== undefined && emp.weeklyDaysOff.length > 0) {
      isDefaultDayOff = emp.weeklyDaysOff.includes(dateObj.getDay());
    } else {
      const workHoursStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, dayName);
      isDefaultDayOff = workHoursStr === '0' || workHoursStr.startsWith('OFF:');
    }

    // Check if date is a company holiday
    const isCompanyHoliday = state.holidays.some(h => h.date === dateString);
    const companyHolidayName = state.holidays.find(h => h.date === dateString)?.name || '';

    // Check if date is a special workday
    const isSpecialWorkday = state.specialWorkdays && state.specialWorkdays.some(s => s.date === dateString);
    const specialWorkdayName = state.specialWorkdays && state.specialWorkdays.find(s => s.date === dateString)?.name || '';

    // Check if date is a compensatory holiday
    const isCompensatoryHoliday = state.specialWorkdays && state.specialWorkdays.some(s => s.compensatoryOffDate === dateString);
    const compensatoryHolidayName = state.specialWorkdays && state.specialWorkdays.find(s => s.compensatoryOffDate === dateString)?.name || '';

    let dayEventName = companyHolidayName || specialWorkdayName || compensatoryHolidayName;

    // Auto-detect status if blank and newly created timesheet, or if it's an untouched row
    let displayStatus = dayData.status;
    const isRowUntouched = ts.isPhantom && (!dayData.timeIn && !dayData.timeOut && !dayData.advance && (dayData.overrideAmount === undefined || dayData.overrideAmount === null || dayData.overrideAmount === '') && !dayData.remark && (!dayData.lateMinutes || parseInt(dayData.lateMinutes) === 0));

    if (!ts.days[d] || (!isPastMonth && isRowUntouched)) {
      const todayString = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      if (emp.startDate && dateString < emp.startDate) displayStatus = 'not_started';
      else if (emp.resignDate && dateString > emp.resignDate) displayStatus = 'resigned';
      else if (dateString > todayString) displayStatus = 'not_started';
      else if (isSpecialWorkday) displayStatus = 'special_workday';
      else if (isCompensatoryHoliday) displayStatus = 'compensatory_holiday';
      else if (isCompanyHoliday) displayStatus = 'holiday';
      else if (isDefaultDayOff) displayStatus = 'day_off';
      else displayStatus = 'work';
    }

    const selectHTML = `
      <select class="grid-day-status" data-day="${d}" style="padding: 4px; font-size: 0.8rem; font-weight:600; width:100%; min-width: 170px;" ${ts.isLocked ? 'disabled' : ''} onchange="onGridStatusChange(this, '${empId}')">
        ${statusOptions.map(opt => `<option value="${opt.value}" ${displayStatus === opt.value ? 'selected' : ''}>${opt.label}</option>`).join('')}
      </select>
    `;

    let defaultTimeIn = '';
    let defaultTimeOut = '';
    const dayWorkHoursStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, dayName);
    let workHours = parseWorkHoursRange(dayWorkHoursStr);

    if (workHours === 0) {
      for (const fallbackDay of dayOfWeekNames) {
        const fallbackStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, fallbackDay);
        const fallbackHours = parseWorkHoursRange(fallbackStr);
        if (fallbackHours > 0) {
          let cleanStr = fallbackStr;
          if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
          const [startStr, endStr] = cleanStr.split('-');
          defaultTimeIn = startStr;
          defaultTimeOut = endStr;
          break;
        }
      }
    } else if (dayWorkHoursStr && dayWorkHoursStr.includes('-')) {
      let cleanStr = dayWorkHoursStr;
      if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
      const [startStr, endStr] = cleanStr.split('-');
      defaultTimeIn = startStr;
      defaultTimeOut = endStr;
    }

    let timeInVal = dayData.timeIn || '';
    let timeOutVal = dayData.timeOut || '';

    if (!dayData.timeIn && !dayData.timeOut) {
      if ((emp.startDate && dateString < emp.startDate) || (emp.resignDate && dateString > emp.resignDate) || displayStatus === 'not_started' || displayStatus === 'day_off') {
        timeInVal = '';
        timeOutVal = '';
      } else {
        timeInVal = defaultTimeIn;
        timeOutVal = defaultTimeOut;
      }
    }
    const advanceVal = dayData.advance || 0;
    const overrideVal = (dayData.overrideAmount !== undefined && dayData.overrideAmount !== null) ? dayData.overrideAmount : '';
    const lateMinutesVal = dayData.lateMinutes || '';
    const remarkVal = dayData.remark || '';

    let isPaidUI = false;
    if (displayStatus === 'work' || displayStatus === 'holiday_selected' || displayStatus === 'special_workday' || displayStatus === 'holiday') {
      isPaidUI = true;
    } else if (emp.type === 'daily' || emp.type === 'freelance') {
      if (state.activeCompanySettings.enableSickLeaveCalc && displayStatus === 'leave_sick') isPaidUI = true;
      if (state.activeCompanySettings.enableAnnualLeaveCalc && displayStatus === 'leave_annual') isPaidUI = true;
    }

    const defaultOverrideText = (emp.type === 'daily' || emp.type === 'freelance') ? (isPaidUI ? effectiveSalaryRate : '0') : 'ตามจริง';
    const dayNameStr = dateObj.toLocaleDateString('th-TH', { weekday: 'short' });

    const isTimeInEdited = timeInVal !== '' && defaultTimeIn !== '' && timeInVal !== defaultTimeIn;
    const isTimeOutEdited = timeOutVal !== '' && defaultTimeOut !== '' && timeOutVal !== defaultTimeOut;

    let rowBgStyle = dayName === 'saturday' ? 'background-color: #e0f2fe;' : '';
    if (isTimeInEdited && isTimeOutEdited) {
      rowBgStyle = 'background: linear-gradient(90deg, rgba(131, 56, 236, 0.15) 0%, rgba(251, 86, 7, 0.15) 100%);';
    } else if (isTimeInEdited) {
      rowBgStyle = 'background: rgba(131, 56, 236, 0.15);';
    } else if (isTimeOutEdited) {
      rowBgStyle = 'background: rgba(251, 86, 7, 0.15);';
    }
    const isZeroOverride = overrideVal === 0 || overrideVal === '0' || (overrideVal === '' && defaultOverrideText === '0');

    gridRowsHTML += `
      <tr class="grid-day-row" data-day="${d}" style="${rowBgStyle}">
        <td style="font-weight: 700;">วันที่ ${d} (${dayNameStr}) ${dayEventName ? `<br><small style="color: var(--danger); font-weight:normal;">${dayEventName}</small>` : ''}</td>
        <td>${selectHTML}</td>
        <td>
          <input type="text" class="time-input-24h grid-day-time-in" data-day="${d}" value="${timeInVal}" placeholder="10:30" style="padding: 4px; font-size:0.8rem; text-align:center; width:65px;" ${ts.isLocked ? 'disabled' : ''} oninput="const f = this.closest('tr').querySelector('.grid-day-override'); if(f) f.value = '';" onchange="recalculateGridTotals('${empId}')">
        </td>
        <td>
          <input type="text" class="time-input-24h grid-day-time-out" data-day="${d}" value="${timeOutVal}" placeholder="18:00" style="padding: 4px; font-size:0.8rem; text-align:center; width:65px;" ${ts.isLocked ? 'disabled' : ''} oninput="const f = this.closest('tr').querySelector('.grid-day-override'); if(f) f.value = '';" onchange="recalculateGridTotals('${empId}')">
        </td>
        <td>
          <input type="number" class="grid-day-late-minutes" data-day="${d}" value="${lateMinutesVal}" placeholder="0" min="0" style="padding: 4px; font-size:0.8rem; text-align:center; width:65px; border:1px solid var(--danger); border-radius:4px; color:var(--danger);" ${ts.isLocked ? 'disabled' : ''} onchange="recalculateGridTotals('${empId}')">
        </td>
        <td>
          ${canView ? `<input type="number" class="grid-day-advance" data-day="${d}" data-prev-value="${advanceVal}" value="${advanceVal}" min="0" style="padding: 4px; font-size:0.8rem; text-align:center; width:80px;" ${ts.isLocked ? 'disabled' : ''} onchange="validateDayAdvance('${empId}', ${d}, this)">` : '<div style="text-align:center;">****</div>'}
        </td>
        <td>
          ${canView ? `<input type="number" class="grid-day-override" data-day="${d}" value="${overrideVal}" placeholder="${defaultOverrideText}" min="0" style="padding: 4px; font-size:0.8rem; text-align:center; width:80px; background:${isZeroOverride ? '#e2e8f0' : 'var(--white)'}; color:${isZeroOverride ? '#94a3b8' : 'inherit'}; border:1px solid ${isZeroOverride ? '#cbd5e1' : 'var(--primary-dark)'};" ${ts.isLocked ? 'disabled' : ''} onchange="recalculateGridTotals('${empId}')">` : '<div style="text-align:center;">****</div>'}
        </td>
        <td>
          <input type="text" class="grid-day-remark" data-day="${d}" value="${remarkVal}" placeholder="หมายเหตุ" style="padding: 4px 8px; font-size:0.8rem; text-align:left; width:100%; min-width:180px; box-sizing:border-box; border:1px solid var(--gray-300); border-radius:4px;" ${ts.isLocked ? 'disabled' : ''} onchange="recalculateGridTotals('${empId}')">
        </td>
      </tr>
    `;
  }

  const dedSettings = getEmployeeDeductionSettings(emp, state.activeCompanySettings);

  const overrideSalary = ts.overrides && ts.overrides.salaryRate !== undefined ? ts.overrides.salaryRate : '';
  const overrideSS = ts.overrides && ts.overrides.socialSecurityRate !== undefined ? ts.overrides.socialSecurityRate : '';
  const overrideSSAmount = ts.overrides && ts.overrides.socialSecurityAmount !== undefined ? ts.overrides.socialSecurityAmount : '';
  const overrideTax = ts.overrides && ts.overrides.withholdingTaxRate !== undefined ? ts.overrides.withholdingTaxRate : '';
  const overrideTaxAmount = ts.overrides && ts.overrides.withholdingTaxAmount !== undefined ? ts.overrides.withholdingTaxAmount : '';
  const overrideStudentLoan = ts.overrides && ts.overrides.studentLoanAmount !== undefined ? ts.overrides.studentLoanAmount : '';
  const overrideSecurityDeposit = ts.overrides && ts.overrides.securityDepositAmount !== undefined ? ts.overrides.securityDepositAmount : '';
  const overrideSecurityDepositRefund = ts.overrides && ts.overrides.securityDepositRefundAmount !== undefined ? ts.overrides.securityDepositRefundAmount : '';
  const overrideDiligenceAllowance = ts.overrides && ts.overrides.diligenceAllowance !== undefined ? ts.overrides.diligenceAllowance : '';
  const hasOverrides = overrideSalary !== '' || overrideSS !== '' || overrideSSAmount !== '' || overrideTax !== '' || overrideTaxAmount !== '' || overrideStudentLoan !== '' || overrideSecurityDeposit !== '' || overrideSecurityDepositRefund !== '' || overrideDiligenceAllowance !== '';

  let overridesHTML = '';
  const _overrideNow = new Date();
  const _overrideCurrentMonthStr = `${_overrideNow.getFullYear()}-${String(_overrideNow.getMonth() + 1).padStart(2, '0')}`;
  const _overrideIsPastMonth = state.selectedMonth < _overrideCurrentMonthStr;

  if (canView && !ts.isLocked && window.checkPermission('edit_timesheet') && _overrideIsPastMonth) {
    overridesHTML = `
      <div class="glass-card" style="margin-bottom: 20px; padding: 15px; border-left: 4px solid var(--warning);">
        <div style="display:flex; justify-content:space-between; align-items:center; cursor:pointer;" onclick="const b = document.getElementById('override-panel-body'); b.style.display = b.style.display === 'none' ? 'block' : 'none';">
          <h4 style="margin:0; font-weight:700; color:var(--text-main);">⚙️ ตั้งค่าฐานเงินเดือนและหักภาษี (เฉพาะใบเวลาทำงานเดือนนี้)</h4>
          <span style="font-size:0.85rem; color:var(--primary); font-weight:bold;">คลิกเพื่อเปิด/ปิด ▾</span>
        </div>
        <div id="override-panel-body" style="display:${hasOverrides ? 'block' : 'none'}; margin-top:15px; padding-top:15px; border-top:1px dashed var(--gray-300);">
          <p style="font-size:0.85rem; color:var(--text-sub); margin-bottom:15px; line-height: 1.5;">หากคุณกำลังกรอกข้อมูลย้อนหลัง และพนักงานมีฐานเงินเดือนหรืออัตราการหักในอดีตที่ไม่ตรงกับปัจจุบัน คุณสามารถระบุค่าเพื่อใช้ <strong>เฉพาะเดือนนี้</strong> ได้ โดยค่าที่แก้ไขที่นี่จะไม่กระทบกับโปรไฟล์พนักงานหลักในปัจจุบัน</p>
          <div style="display:flex; gap:15px; flex-wrap:wrap;">
            <div style="flex:1; min-width:200px;">
              <label style="font-size:0.85rem; display:block; margin-bottom:5px; font-weight:bold;">ฐานเงินเดือนเฉพาะเดือนนี้</label>
              <input type="number" id="override-salary-rate" value="${overrideSalary}" placeholder="ค่าปัจจุบัน: ${emp.salaryRate || 0}" style="width:100%; padding:8px 12px; border:1px solid var(--gray-300); border-radius:4px; font-size:0.9rem;">
            </div>
            <div style="flex:1; min-width:200px;">
              <label style="font-size:0.85rem; display:block; margin-bottom:5px; font-weight:bold;">% หักประกันสังคมเฉพาะเดือนนี้</label>
              <input type="number" id="override-ss-rate" value="${overrideSS}" placeholder="ค่าปัจจุบัน: ${dedSettings.socialSecurity.rate}" style="width:100%; padding:8px 12px; border:1px solid var(--gray-300); border-radius:4px; font-size:0.9rem;">
            </div>
            <div style="flex:1; min-width:200px;">
              <label style="font-size:0.85rem; display:block; margin-bottom:5px; font-weight:bold;">ยอดหักประกันสังคม (บาท) เดือนนี้</label>
              <input type="number" id="override-ss-amount" value="${overrideSSAmount}" placeholder="ยอดสุทธิ (ข้ามการคำนวณ %)" style="width:100%; padding:8px 12px; border:1px solid var(--gray-300); border-radius:4px; font-size:0.9rem;">
            </div>
            <div style="flex:1; min-width:200px;">
              <label style="font-size:0.85rem; display:block; margin-bottom:5px; font-weight:bold;">% หักภาษี ณ ที่จ่ายเฉพาะเดือนนี้</label>
              <input type="number" id="override-tax-rate" value="${overrideTax}" placeholder="ค่าปัจจุบัน: ${dedSettings.withholdingTax.rate}" style="width:100%; padding:8px 12px; border:1px solid var(--gray-300); border-radius:4px; font-size:0.9rem;">
            </div>
            <div style="flex:1; min-width:200px;">
              <label style="font-size:0.85rem; display:block; margin-bottom:5px; font-weight:bold;">ยอดหักภาษี ณ ที่จ่าย (บาท) เดือนนี้</label>
              <input type="number" id="override-tax-amount" value="${overrideTaxAmount}" placeholder="ยอดสุทธิ (ข้ามการคำนวณ %)" style="width:100%; padding:8px 12px; border:1px solid var(--gray-300); border-radius:4px; font-size:0.9rem;">
            </div>
            <div style="flex:1; min-width:200px;">
              <label style="font-size:0.85rem; display:block; margin-bottom:5px; font-weight:bold;">ยอดหัก กยศ. เฉพาะเดือนนี้</label>
              <input type="number" id="override-student-loan" value="${overrideStudentLoan}" placeholder="ค่าปัจจุบัน: ${emp.studentLoanFund?.enabled ? emp.studentLoanFund.monthlyAmount : 0}" style="width:100%; padding:8px 12px; border:1px solid var(--gray-300); border-radius:4px; font-size:0.9rem;">
            </div>
            <div style="flex:1; min-width:200px;">
              <label style="font-size:0.85rem; display:block; margin-bottom:5px; font-weight:bold;">ยอดเบี้ยขยัน (บาท) เฉพาะเดือนนี้</label>
              <input type="number" id="override-diligence-allowance" value="${overrideDiligenceAllowance}" placeholder="ค่าปัจจุบัน: ${state.activeCompanySettings?.enableDiligenceAllowance !== false ? emp.diligenceAllowance || 0 : 0}" style="width:100%; padding:8px 12px; border:1px solid var(--gray-300); border-radius:4px; font-size:0.9rem;">
            </div>
            <div style="flex:1; min-width:200px;">
              <label style="font-size:0.85rem; display:block; margin-bottom:5px; font-weight:bold;">ยอดหักเงินประกัน เฉพาะเดือนนี้</label>
              <input type="number" id="override-security-deposit" value="${overrideSecurityDeposit}" placeholder="ค่าปัจจุบัน (ถ้าหัก): ${emp.securityDepositAmount || 0}" style="width:100%; padding:8px 12px; border:1px solid var(--gray-300); border-radius:4px; font-size:0.9rem;">
            </div>
            <div style="flex:1; min-width:200px;">
              <label style="font-size:0.85rem; display:block; margin-bottom:5px; font-weight:bold;">ยอดคืนเงินประกัน เฉพาะเดือนนี้</label>
              <input type="number" id="override-security-refund" value="${overrideSecurityDepositRefund}" placeholder="ค่าปัจจุบัน (ถ้าคืน): ${emp.securityDepositRefundAmount !== undefined ? emp.securityDepositRefundAmount : (emp.securityDepositAmount || 0)}" style="width:100%; padding:8px 12px; border:1px solid var(--gray-300); border-radius:4px; font-size:0.9rem;">
            </div>
          </div>
        </div>
      </div>
    `;
  }

  wrapper.innerHTML = `
    <div style="width: 98%; max-width: 1450px; margin: 0 auto; padding-bottom: 50px;">
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom: 20px;">
        <h3 style="font-weight:700; color:var(--primary-dark); margin:0;">บันทึกเวลาทำงานรายวัน: ${escapeHtml(emp.realName)} (${escapeHtml(state.selectedMonth)})</h3>
      </div>
      
      ${lockWarningHTML}
      ${overridesHTML}
      
      <div style="display: flex; gap: 20px; align-items: flex-start; flex-wrap: wrap;">
        <!-- Left Table Column -->
        <div style="flex: 3; min-width: 1050px; background: var(--card-bg); border-radius: var(--border-radius-md); box-shadow: var(--shadow); overflow: hidden;">
          <div class="timesheet-grid-wrapper" style="overflow-x: auto;">
            <table class="timesheet-table" style="width: 100%; border-collapse: collapse;">
              <thead>
                <tr style="background: var(--gray-200); border-bottom: 2px solid var(--gray-300);">
                  <th style="padding: 12px 10px; text-align: left;">วัน</th>
                  <th style="padding: 12px 10px; text-align: left; min-width: 170px;">สถานะการเข้างาน</th>
                  <th style="padding: 12px 10px; text-align: center;">เวลาเข้า (24h)</th>
                  <th style="padding: 12px 10px; text-align: center;">เวลาออก (24h)</th>
                  <th style="padding: 12px 10px; text-align: center; color:var(--danger);">มาสาย (นาที)</th>
                  <th style="padding: 12px 10px; text-align: center;">เบิกล่วงหน้า (บาท)</th>
                  <th style="padding: 12px 10px; text-align: center;">ยอดจริง/วัน</th>
                  <th style="padding: 12px 10px; text-align: left; min-width: 240px;">หมายเหตุ</th>
                </tr>
              </thead>
              <tbody id="interactive-grid-body">
                ${gridRowsHTML}
              </tbody>
            </table>
          </div>
        </div>
 
        <!-- Right Calculation Box -->
        <div style="flex: 1; min-width: 320px; max-width: 400px; background: var(--gray-200); padding: 25px; border-radius: var(--border-radius-md); box-shadow: var(--shadow); position: sticky; top: 20px;">
          <h4 style="font-weight: 700; color: var(--primary-dark); margin-bottom: 15px; border-bottom:1px solid var(--gray-300); padding-bottom:8px;">คำนวณเบื้องต้นสิ้นเดือน</h4>
          <div style="display: flex; flex-direction: column; gap: 10px; font-size: 0.95rem;">
            <div style="display:flex; justify-content:space-between;"><span>💵 เงินได้สะสม:</span><strong id="calc-basic-earned">${canView ? summ.basicSalaryEarned.toLocaleString() + ' บาท' : '****'}</strong></div>
              <div id="calc-diligence-row" style="display:${state.activeCompanySettings.enableDiligenceAllowance !== false ? 'flex' : 'none'}; justify-content:space-between;"><span>🌟 เบี้ยขยัน:</span><strong id="calc-diligence" style="color:var(--success);">${canView ? summ.diligenceAllowance.toLocaleString() + ' บาท' : '****'}</strong></div>
              <div style="display:flex; justify-content:space-between;"><span>⏰ หักมาสาย:</span><strong id="calc-late" style="color:var(--danger);">${canView ? summ.lateDeduction.toLocaleString() + ' บาท' : '****'}</strong></div>
              <div id="calc-withholding-tax-row" style="display:${(overrideTax !== '' ? true : dedSettings.withholdingTax.enabled) ? 'flex' : 'none'}; justify-content:space-between;">
                <span>🛡️ หักภาษี ณ ที่จ่าย (${overrideTax !== '' ? overrideTax : dedSettings.withholdingTax.rate}%):</span>
                <strong id="calc-withholding-tax">${canView ? summ.withholdingTaxDeducted.toLocaleString() + ' บาท' : '****'}</strong>
              </div>
              <div id="calc-social-security-row" style="display:${(overrideSS !== '' ? true : dedSettings.socialSecurity.enabled) ? 'flex' : 'none'}; justify-content:space-between;">
                <span>🛡️ หักประกันสังคม (${overrideSS !== '' ? overrideSS : dedSettings.socialSecurity.rate}%):</span>
                <strong id="calc-social-security">${canView ? summ.socialSecurityDeducted.toLocaleString() + ' บาท' : '****'}</strong>
              </div>
              <div style="display:flex; justify-content:space-between;"><span>🎓 หัก กยศ:</span><strong id="calc-loan">${canView ? summ.studentLoanDeducted.toLocaleString() + ' บาท' : '****'}</strong></div>
              <div style="display:flex; justify-content:space-between;"><span>🔒 หักเงินประกัน:</span><strong id="calc-security">${canView ? summ.securityDepositDeducted.toLocaleString() + ' บาท' : '****'}</strong></div>
              <div id="calc-security-refund-row" style="display:${summ.securityDepositRefunded > 0 ? 'flex' : 'none'}; justify-content:space-between; color: var(--success); font-weight: 700;"><span>🔓 คืนเงินประกัน:</span><strong id="calc-security-refund">${canView ? summ.securityDepositRefunded.toLocaleString() + ' บาท' : '****'}</strong></div>
              <div style="display:flex; justify-content:space-between;"><span>💸 รวมเบิกล่วงหน้า:</span><strong id="calc-advance" style="color:var(--warning);">${canView ? summ.totalAdvance.toLocaleString() + ' บาท' : '****'}</strong></div>
              <div id="calc-other-incomes-row" style="display:${summ.otherIncomes > 0 ? 'flex' : 'none'}; justify-content:space-between;"><span>➕ รับอื่นๆ:</span><strong id="calc-other-incomes" style="color:var(--success);">${canView ? summ.otherIncomes.toLocaleString() + ' บาท' : '****'}</strong></div>
              <div id="calc-other-deductions-row" style="display:${summ.otherDeductions > 0 ? 'flex' : 'none'}; justify-content:space-between;"><span>➖ หักอื่นๆ:</span><strong id="calc-other-deductions" style="color:var(--danger);">${canView ? summ.otherDeductions.toLocaleString() + ' บาท' : '****'}</strong></div>
              <div style="display:flex; justify-content:space-between; border-top:1px dashed var(--gray-400); padding-top:10px; font-size:1.05rem;"><span>💰 รับสุทธิสิ้นเดือน:</span><strong id="calc-net" style="color:var(--primary-dark);">${canView ? summ.netSalary.toLocaleString() + ' บาท' : '****'}</strong></div>
            </div>
            
            <div style="margin-top: 15px; background: white; padding: 15px; border-radius: var(--border-radius-sm); border: 1px solid var(--gray-300);">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                <h5 style="margin: 0; font-weight: 700; font-size: 0.95rem;">💵 รายการรับ/หักเพิ่มเติม</h5>
                ${canView && window.checkPermission('edit_payroll_adjustments') && !ts.isLocked ? `<button type="button" class="btn btn-secondary btn-sm" onclick="addTimesheetAdjustmentRow()">➕ เพิ่ม</button>` : ''}
              </div>
              <div id="timesheet-adjustments-list">
                ${adjustmentsHTML}
              </div>
            </div>
            
            <div style="margin-top: 20px;">
              ${window.checkPermission('edit_timesheet') ? `
                ${ts.isLocked ? `
                  <button class="btn btn-secondary" onclick="unlockTimesheetTrigger('${emp.id}')" style="width:100%; margin-bottom:10px;">🔓 ปลดล็อกแก้ไขงวดนี้</button>
                ` : `
                  <button class="btn btn-primary" onclick="saveTimesheetTrigger('${emp.id}')" style="width:100%; margin-bottom:10px;">💾 บันทึกเวลาทำงาน</button>
                  <button class="btn btn-secondary" onclick="lockTimesheetTrigger('${emp.id}')" style="width:100%; margin-bottom:10px;">🔒 ยืนยันจ่ายเงินและล็อกประวัติ</button>
                `}
              ` : '🔒 คุณไม่มีสิทธิ์แก้ไขข้อมูลเวลางาน'}
              <button class="btn btn-secondary" onclick="goBackFromTimesheet()" style="width:100%;">⬅️ ย้อนกลับ</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  // Prime dynamic advance limit hints immediately
  recalculateGridTotals(empId, ts.isLocked);
};

// Client-side instant recalculator for interactive timesheet modal
function recalculateGridTotals (empId, isLocked = false) {
  if (isLocked) return;
  const emp = state.employees.find(e => e.id === empId);

  const overrideSalaryInput = document.getElementById('override-salary-rate');
  const effectiveSalaryRate = overrideSalaryInput && overrideSalaryInput.value !== '' ? parseFloat(overrideSalaryInput.value) : (emp.salaryRate || 0);

  const daysRows = document.querySelectorAll('.grid-day-row');

  // Accumulate dummy timesheet from inputs
  const days = {};
  daysRows.forEach(row => {
    const d = row.getAttribute('data-day');
    const status = row.querySelector('.grid-day-status').value;
    const timeIn = row.querySelector('.grid-day-time-in').value;
    const timeOut = row.querySelector('.grid-day-time-out').value;
    const lateMinutesInput = row.querySelector('.grid-day-late-minutes');
    const lateMinutes = lateMinutesInput && lateMinutesInput.value !== '' ? parseInt(lateMinutesInput.value) : 0;
    const advance = parseFloat(row.querySelector('.grid-day-advance').value || 0);
    const overrideField = row.querySelector('.grid-day-override');
    let overrideInput = overrideField ? overrideField.value : '';
    let overrideAmount = overrideInput !== '' ? parseFloat(overrideInput) : null;
    const remarkInput = row.querySelector('.grid-day-remark');
    const remark = remarkInput ? remarkInput.value.trim() : '';

    if (overrideField) {
      let isPaidUI = false;
      if (status === 'work' || status === 'holiday_selected' || status === 'special_workday' || status === 'holiday') {
        isPaidUI = true;
      } else if (emp.type === 'daily' || emp.type === 'freelance') {
        if (state.activeCompanySettings.enableSickLeaveCalc && status === 'leave_sick') isPaidUI = true;
        if (state.activeCompanySettings.enableAnnualLeaveCalc && status === 'leave_annual') isPaidUI = true;
      }
      const defaultText = (emp.type === 'daily' || emp.type === 'freelance') ? (isPaidUI ? effectiveSalaryRate : '0') : 'ตามจริง';
      overrideField.placeholder = defaultText;

      if (!isPaidUI && overrideInput !== '') {
        overrideField.value = '';
        overrideInput = '';
        overrideAmount = null;
      }
    }

    const [yearStr, monthStr] = state.selectedMonth.split('-');
    const dateObj = new Date(parseInt(yearStr), parseInt(monthStr) - 1, parseInt(d));
    const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayOfWeekNames[dateObj.getDay()];

    let defaultTimeIn = '';
    let defaultTimeOut = '';
    const dayWorkHoursStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, dayName);
    let workHours = parseWorkHoursRange(dayWorkHoursStr);

    if (workHours === 0) {
      for (const fallbackDay of dayOfWeekNames) {
        const fallbackStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, fallbackDay);
        const fallbackHours = parseWorkHoursRange(fallbackStr);
        if (fallbackHours > 0) {
          let cleanStr = fallbackStr;
          if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
          const [startStr, endStr] = cleanStr.split('-');
          defaultTimeIn = startStr;
          defaultTimeOut = endStr;
          break;
        }
      }
    } else if (dayWorkHoursStr && dayWorkHoursStr.includes('-')) {
      let cleanStr = dayWorkHoursStr;
      if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
      const [startStr, endStr] = cleanStr.split('-');
      defaultTimeIn = startStr;
      defaultTimeOut = endStr;
    }

    const isTimeInEdited = timeIn !== '' && defaultTimeIn !== '' && timeIn !== defaultTimeIn;
    const isTimeOutEdited = timeOut !== '' && defaultTimeOut !== '' && timeOut !== defaultTimeOut;

    if (isTimeInEdited && isTimeOutEdited) {
      row.style.background = 'linear-gradient(90deg, rgba(131, 56, 236, 0.15) 0%, rgba(251, 86, 7, 0.15) 100%)';
    } else if (isTimeInEdited) {
      row.style.background = 'rgba(131, 56, 236, 0.15)';
    } else if (isTimeOutEdited) {
      row.style.background = 'rgba(251, 86, 7, 0.15)';
    } else {
      row.style.background = dayName === 'saturday' ? '#e0f2fe' : '';
    }

    if ((emp.type === 'daily' || emp.type === 'freelance') && (status === 'work' || status === 'holiday_selected' || status === 'special_workday' || status === 'holiday') && timeIn && timeOut) {
      if (defaultTimeIn && defaultTimeOut) {
        const [inH, inM] = timeIn.split(':').map(Number);
        const [startH, startM] = defaultTimeIn.split(':').map(Number);
        const [outH, outM] = timeOut.split(':').map(Number);
        const [endH, endM] = defaultTimeOut.split(':').map(Number);

        let missingMins = 0;
        if (!isNaN(inH) && !isNaN(inM) && !isNaN(startH) && !isNaN(startM)) {
          const inTotal = inH * 60 + inM;
          const startTotal = startH * 60 + startM;
          if (inTotal > startTotal) missingMins += (inTotal - startTotal);
        }
        if (!isNaN(outH) && !isNaN(outM) && !isNaN(endH) && !isNaN(endM)) {
          const outTotal = outH * 60 + outM;
          const endTotal = endH * 60 + endM;
          if (outTotal < endTotal) missingMins += (endTotal - outTotal);
        }

        if (missingMins > 0) {
          const workHours = (endH * 60 + endM - (startH * 60 + startM)) / 60;
          if (workHours > 0) {
            const minuteRate = effectiveSalaryRate / workHours / 60;
            let netDaily = effectiveSalaryRate - (missingMins * minuteRate);
            if (netDaily < 0) netDaily = 0;

            // Auto update override input if not already explicitly set
            const overrideField = row.querySelector('.grid-day-override');
            if (overrideInput === '') {
              if (state.activeCompanySettings.partialDayRounding === 'decimal') {
                overrideAmount = Math.round(netDaily * 100) / 100;
              } else {
                overrideAmount = Math.floor(netDaily);
              }
              if (overrideField) overrideField.value = overrideAmount;
            }
          }
        }
      }
    }

    if (overrideField) {
      const finalVal = overrideField.value;
      const isZeroDynamic = finalVal === '0' || finalVal === 0 || (finalVal === '' && overrideField.placeholder === '0');
      if (isZeroDynamic) {
        overrideField.style.background = '#e2e8f0';
        overrideField.style.color = '#94a3b8';
        overrideField.style.borderColor = '#cbd5e1';
      } else {
        overrideField.style.background = 'var(--white)';
        overrideField.style.color = '';
        overrideField.style.borderColor = 'var(--primary-dark)';
      }
    }

    days[d] = { status, lateMinutes, earlyLeaveMinutes: 0, advance, timeIn, timeOut, overrideAmount, remark };
  });

  const recurringItems = [];
  const manualItems = [];
  document.querySelectorAll('.adj-item-row').forEach(row => {
    const name = row.querySelector('.adj-name').value.trim();
    const type = row.querySelector('.adj-type').value;
    const amount = parseFloat(row.querySelector('.adj-amount').value || 0);
    const recurrence = row.querySelector('.adj-recurrence').value;
    if (name && amount > 0) {
      if (recurrence === 'recurring') recurringItems.push({ name, type, amount });
      else manualItems.push({ name, type, amount });
    }
  });

  if (emp.recurringItems) {
    const diligence = emp.recurringItems.find(item => item.name === 'เบี้ยขยัน');
    if (diligence) recurringItems.push(diligence);
  }
  const originalRecurring = emp.recurringItems;
  emp.recurringItems = recurringItems;

  const overrideSSInput = document.getElementById('override-ss-rate');
  const overrideSSAmountInput = document.getElementById('override-ss-amount');
  const overrideTaxInput = document.getElementById('override-tax-rate');
  const overrideTaxAmountInput = document.getElementById('override-tax-amount');
  const overrideStudentLoanInput = document.getElementById('override-student-loan');
  const overrideSecurityDepositInput = document.getElementById('override-security-deposit');
  const overrideSecurityRefundInput = document.getElementById('override-security-refund');
  const overrideDiligenceAllowanceInput = document.getElementById('override-diligence-allowance');

  const overrides = {};
  if (overrideSalaryInput && overrideSalaryInput.value !== '') overrides.salaryRate = overrideSalaryInput.value;
  if (overrideSSInput && overrideSSInput.value !== '') overrides.socialSecurityRate = overrideSSInput.value;
  if (overrideSSAmountInput && overrideSSAmountInput.value !== '') overrides.socialSecurityAmount = overrideSSAmountInput.value;
  if (overrideTaxInput && overrideTaxInput.value !== '') overrides.withholdingTaxRate = overrideTaxInput.value;
  if (overrideTaxAmountInput && overrideTaxAmountInput.value !== '') overrides.withholdingTaxAmount = overrideTaxAmountInput.value;
  if (overrideStudentLoanInput && overrideStudentLoanInput.value !== '') overrides.studentLoanAmount = overrideStudentLoanInput.value;
  if (overrideSecurityDepositInput && overrideSecurityDepositInput.value !== '') overrides.securityDepositAmount = overrideSecurityDepositInput.value;
  if (overrideSecurityRefundInput && overrideSecurityRefundInput.value !== '') overrides.securityDepositRefundAmount = overrideSecurityRefundInput.value;
  if (overrideDiligenceAllowanceInput && overrideDiligenceAllowanceInput.value !== '') overrides.diligenceAllowance = overrideDiligenceAllowanceInput.value;

  const dummyTimesheet = {
    employeeId: empId,
    month: state.selectedMonth,
    days,
    manualItems,
    overrides: Object.keys(overrides).length > 0 ? overrides : null,
    isLocked: false // Temporary
  };

  const summ = calculatePayrollForEmployee(emp, dummyTimesheet, state.activeCompanySettings);
  emp.recurringItems = originalRecurring;

  // Update calculation box elements
  document.getElementById('calc-basic-earned').textContent = `${summ.basicSalaryEarned.toLocaleString()} บาท`;
  document.getElementById('calc-diligence').textContent = `${summ.diligenceAllowance.toLocaleString()} บาท`;
  document.getElementById('calc-late').textContent = `${summ.lateDeduction.toLocaleString()} บาท`;
  const dedSettings = getEmployeeDeductionSettings(emp, state.activeCompanySettings);

  const hasTaxOverride = dummyTimesheet.overrides && dummyTimesheet.overrides.withholdingTaxRate !== undefined;
  const isTaxEnabled = hasTaxOverride ? true : dedSettings.withholdingTax.enabled;

  const hasSSOverride = dummyTimesheet.overrides && dummyTimesheet.overrides.socialSecurityRate !== undefined;
  const isSSEnabled = hasSSOverride ? true : dedSettings.socialSecurity.enabled;

  const withholdingRow = document.getElementById('calc-withholding-tax-row');
  if (withholdingRow) withholdingRow.style.display = isTaxEnabled ? 'flex' : 'none';
  const withholdingVal = document.getElementById('calc-withholding-tax');
  if (withholdingVal) withholdingVal.textContent = `${summ.withholdingTaxDeducted.toLocaleString()} บาท`;

  const ssRow = document.getElementById('calc-social-security-row');
  if (ssRow) ssRow.style.display = isSSEnabled ? 'flex' : 'none';
  const ssVal = document.getElementById('calc-social-security');
  if (ssVal) ssVal.textContent = `${summ.socialSecurityDeducted.toLocaleString()} บาท`;
  document.getElementById('calc-loan').textContent = `${summ.studentLoanDeducted.toLocaleString()} บาท`;
  document.getElementById('calc-security').textContent = `${summ.securityDepositDeducted.toLocaleString()} บาท`;
  const refundRow = document.getElementById('calc-security-refund-row');
  if (refundRow) refundRow.style.display = summ.securityDepositRefunded > 0 ? 'flex' : 'none';
  const refundVal = document.getElementById('calc-security-refund');
  if (refundVal) refundVal.textContent = `${summ.securityDepositRefunded.toLocaleString()} บาท`;
  document.getElementById('calc-advance').textContent = `${summ.totalAdvance.toLocaleString()} บาท`;

  const otherIncRow = document.getElementById('calc-other-incomes-row');
  if (otherIncRow) otherIncRow.style.display = summ.otherIncomes > 0 ? 'flex' : 'none';
  const otherIncVal = document.getElementById('calc-other-incomes');
  if (otherIncVal) otherIncVal.textContent = `${summ.otherIncomes.toLocaleString()} บาท`;

  const otherDedRow = document.getElementById('calc-other-deductions-row');
  if (otherDedRow) otherDedRow.style.display = summ.otherDeductions > 0 ? 'flex' : 'none';
  const otherDedVal = document.getElementById('calc-other-deductions');
  if (otherDedVal) otherDedVal.textContent = `${summ.otherDeductions.toLocaleString()} บาท`;

  document.getElementById('calc-net').textContent = `${summ.netSalary.toLocaleString()} บาท`;

  // Dynamic advance limits rendering per day
  daysRows.forEach(row => {
    const d = parseInt(row.getAttribute('data-day'));
    const maxVal = calculateMaxAdvanceAllowed(emp, dummyTimesheet, state.activeCompanySettings, d);
    const hintCell = document.getElementById(`grid-max-advance-hint-${d}`);
    if (hintCell) {
      hintCell.innerHTML = `<strong>${maxVal.toLocaleString()}</strong> บาท`;
    }
  });
};

// Validate that advance money on day D does not exceed max allowed (Requirement 6)
async function validateDayAdvance (empId, dayNumber, inputElem) {
  const emp = state.employees.find(e => e.id === empId);
  const enteredVal = parseFloat(inputElem.value || 0);

  if (enteredVal <= 0) {
    inputElem.dataset.prevValue = 0;
    recalculateGridTotals(empId);
    return;
  }

  // Prepare dummy days mapping
  const daysRows = document.querySelectorAll('.grid-day-row');
  const days = {};
  daysRows.forEach(row => {
    const d = row.getAttribute('data-day');
    const status = row.querySelector('.grid-day-status').value;
    const timeIn = row.querySelector('.grid-day-time-in').value;
    const timeOut = row.querySelector('.grid-day-time-out').value;
    const lateMinutesInput = row.querySelector('.grid-day-late-minutes');
    const lateMinutes = lateMinutesInput && lateMinutesInput.value !== '' ? parseInt(lateMinutesInput.value) : 0;
    // Use the newly entered value for our validator
    const advance = d == dayNumber ? enteredVal : parseFloat(row.querySelector('.grid-day-advance').value || 0);
    const overrideInput = row.querySelector('.grid-day-override').value;
    const overrideAmount = overrideInput !== '' ? parseFloat(overrideInput) : null;

    days[d] = { status, lateMinutes, earlyLeaveMinutes: 0, advance, timeIn, timeOut, overrideAmount };
  });

  const dummyTimesheet = {
    employeeId: empId,
    month: state.selectedMonth,
    days
  };

  const maxLimit = calculateMaxAdvanceAllowed(emp, dummyTimesheet, state.activeCompanySettings, dayNumber);

  if (enteredVal > maxLimit) {
    const excess = enteredVal - maxLimit;
    const prevVal = inputElem.dataset.prevValue !== undefined ? parseFloat(inputElem.dataset.prevValue) : 0;

    const confirmed = await showConfirm({
      title: '⚠️ แจ้งเตือนการเบิกเงินเกินรายได้',
      message: `ยอดเงินที่จะเบิกในวันที่ ${dayNumber} (${enteredVal.toLocaleString()} บาท) เกินวงเงินรายได้สะสมที่เบิกได้ ณ วันนั้น (${maxLimit.toLocaleString()} บาท)\n\n• กำลังเบิกเกิน: ${excess.toLocaleString()} บาท\n\nคุณยังคงยืนยันที่จะเบิกเงินจำนวนนี้หรือไม่? หรือต้องการยกเลิกไม่เบิก?`,
      confirmText: '✅ ยืนยันที่จะเบิก',
      cancelText: '❌ ยกเลิกไม่เบิก',
      isDanger: true,
      icon: '💸'
    });

    if (confirmed) {
      inputElem.dataset.prevValue = enteredVal;
      toast.warning(`บันทึกยอดเบิกเกินรายได้ (เกิน ${excess.toLocaleString()} บาท) เรียบร้อย`);
    } else {
      inputElem.value = prevVal > 0 ? prevVal : 0;
      inputElem.dataset.prevValue = inputElem.value;
    }
  } else {
    inputElem.dataset.prevValue = enteredVal;
  }

  // Recalculate everything
  recalculateGridTotals(empId);
};

function onGridStatusChange (selectElem, empId) {
  const row = selectElem.closest('tr');
  if (!row) return;
  const d = selectElem.getAttribute('data-day');
  const status = selectElem.value;
  const emp = state.employees.find(e => e.id === empId);
  if (!emp) return;

  const [yearStr, monthStr] = state.selectedMonth.split('-');
  const dateObj = new Date(parseInt(yearStr), parseInt(monthStr) - 1, parseInt(d));
  const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayOfWeekNames[dateObj.getDay()];

  let defaultTimeIn = '08:00';
  let defaultTimeOut = '17:00';
  const dayWorkHoursStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, dayName);
  let workHours = parseWorkHoursRange(dayWorkHoursStr);

  if (workHours === 0) {
    for (const fallbackDay of dayOfWeekNames) {
      const fallbackStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, fallbackDay);
      const fallbackHours = parseWorkHoursRange(fallbackStr);
      if (fallbackHours > 0) {
        let cleanStr = fallbackStr;
        if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
        const [startStr, endStr] = cleanStr.split('-');
        defaultTimeIn = startStr;
        defaultTimeOut = endStr;
        break;
      }
    }
  } else if (dayWorkHoursStr && dayWorkHoursStr.includes('-')) {
    let cleanStr = dayWorkHoursStr;
    if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
    const [startStr, endStr] = cleanStr.split('-');
    defaultTimeIn = startStr;
    defaultTimeOut = endStr;
  }

  const timeInInput = row.querySelector('.grid-day-time-in');
  const timeOutInput = row.querySelector('.grid-day-time-out');
  const lateInput = row.querySelector('.grid-day-late-minutes');
  const overrideInput = row.querySelector('.grid-day-override');

  if (status === 'work' || status === 'holiday_selected' || status === 'special_workday' || status === 'holiday') {
    if (timeInInput && !timeInInput.value) timeInInput.value = defaultTimeIn;
    if (timeOutInput && !timeOutInput.value) timeOutInput.value = defaultTimeOut;
  } else if (status === 'day_off' || status === 'not_started' || status === 'resigned' || status === 'absence') {
    if (timeInInput) timeInInput.value = '';
    if (timeOutInput) timeOutInput.value = '';
    if (lateInput) lateInput.value = '';
    if (overrideInput) overrideInput.value = '';
  }

  recalculateGridTotals(empId);
}

async function saveTimesheetTrigger (empId, silent = false) {
  const emp = state.employees.find(e => e.id === empId);
  const oldTimesheet = await getTimesheet(empId, state.selectedMonth);

  const daysRows = document.querySelectorAll('.grid-day-row');
  const days = { ...(oldTimesheet.days || {}) };

  const [yearStr, monthStr] = state.selectedMonth.split('-');
  const year = parseInt(yearStr);
  const month = parseInt(monthStr);
  const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  daysRows.forEach(row => {
    const d = row.getAttribute('data-day');
    const status = row.querySelector('.grid-day-status').value;
    let timeIn = row.querySelector('.grid-day-time-in').value;
    let timeOut = row.querySelector('.grid-day-time-out').value;
    const lateMinutesInput = row.querySelector('.grid-day-late-minutes');
    const lateMinutes = lateMinutesInput && lateMinutesInput.value !== '' ? parseInt(lateMinutesInput.value) : 0;
    
    const advanceInput = row.querySelector('.grid-day-advance');
    const advance = advanceInput ? parseFloat(advanceInput.value || 0) : (oldTimesheet.days?.[d]?.advance || 0);

    const overrideField = row.querySelector('.grid-day-override');
    const overrideInput = overrideField ? overrideField.value : null;
    let overrideAmount = overrideInput !== null && overrideInput !== '' ? parseFloat(overrideInput) : (overrideField ? null : (oldTimesheet.days?.[d]?.overrideAmount ?? null));

    const remarkInput = row.querySelector('.grid-day-remark');
    const remark = remarkInput ? remarkInput.value.trim() : '';

    // Auto-fill default work hours for work status if empty, matching Quick Daily Record
    const dateObj = new Date(year, month - 1, parseInt(d));
    const dayName = dayOfWeekNames[dateObj.getDay()];
    const workHoursStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, dayName);

    let defaultTimeIn = '08:00';
    let defaultTimeOut = '17:00';
    let workHours = parseWorkHoursRange(workHoursStr);

    if (workHours === 0) {
      for (const fallbackDay of dayOfWeekNames) {
        const fallbackStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, fallbackDay);
        const fallbackHours = parseWorkHoursRange(fallbackStr);
        if (fallbackHours > 0) {
          let cleanStr = fallbackStr;
          if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
          const [startStr, endStr] = cleanStr.split('-');
          defaultTimeIn = startStr;
          defaultTimeOut = endStr;
          break;
        }
      }
    } else if (workHoursStr && workHoursStr.includes('-')) {
      let cleanStr = workHoursStr;
      if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
      const [startStr, endStr] = cleanStr.split('-');
      defaultTimeIn = startStr;
      defaultTimeOut = endStr;
    }

    if ((status === 'work' || status === 'holiday_selected' || status === 'special_workday' || status === 'holiday') && (!timeIn || !timeOut)) {
      if (!timeIn) timeIn = defaultTimeIn;
      if (!timeOut) timeOut = defaultTimeOut;
    }

    // Auto-fill overrideAmount for partial work time, exactly mirroring saveQuickDailyRecordTrigger
    if ((emp.type === 'daily' || emp.type === 'freelance') && (status === 'work' || status === 'holiday_selected' || status === 'special_workday' || status === 'holiday') && timeIn && timeOut) {
      const dailyRate = oldTimesheet.overrides && oldTimesheet.overrides.salaryRate !== undefined ? parseFloat(oldTimesheet.overrides.salaryRate) : (emp.salaryRate || 0);

      if (defaultTimeIn && defaultTimeOut) {
        const [inH, inM] = timeIn.split(':').map(Number);
        const [startH, startM] = defaultTimeIn.split(':').map(Number);
        const [outH, outM] = timeOut.split(':').map(Number);
        const [endH, endM] = defaultTimeOut.split(':').map(Number);

        let missingMins = 0;
        if (!isNaN(inH) && !isNaN(inM) && !isNaN(startH) && !isNaN(startM)) {
          const inTotal = inH * 60 + inM;
          const startTotal = startH * 60 + startM;
          if (inTotal > startTotal) missingMins += (inTotal - startTotal);
        }
        if (!isNaN(outH) && !isNaN(outM) && !isNaN(endH) && !isNaN(endM)) {
          const outTotal = outH * 60 + outM;
          const endTotal = endH * 60 + endM;
          if (outTotal < endTotal) missingMins += (endTotal - outTotal);
        }

        if (missingMins > 0 && overrideAmount === null) {
          const calcWorkHours = (endH * 60 + endM - (startH * 60 + startM)) / 60;
          if (calcWorkHours > 0) {
            const minuteRate = dailyRate / calcWorkHours / 60;
            let netDaily = dailyRate - (missingMins * minuteRate);
            if (netDaily < 0) netDaily = 0;
            if (state.activeCompanySettings.partialDayRounding === 'decimal') {
              overrideAmount = Math.round(netDaily * 100) / 100;
            } else {
              overrideAmount = Math.floor(netDaily);
            }
          }
        }
      }
    }

    days[d] = {
      status,
      lateMinutes,
      earlyLeaveMinutes: oldTimesheet.days?.[d]?.earlyLeaveMinutes || 0,
      advance,
      timeIn,
      timeOut,
      overrideAmount,
      remark
    };
  });

  // Handle adjustments (recurring items & manual items)
  let recurringItems = emp.recurringItems || [];
  let manualItems = oldTimesheet.manualItems || [];

  const canView = window.canViewSalary(emp);
  if (canView && window.checkPermission('edit_payroll_adjustments')) {
    const newRecurring = [];
    const newManual = [];
    const adjRows = document.querySelectorAll('.adj-item-row');
    adjRows.forEach(row => {
      const name = row.querySelector('.adj-name').value.trim();
      const type = row.querySelector('.adj-type').value;
      const amount = parseFloat(row.querySelector('.adj-amount').value || 0);
      const recurrence = row.querySelector('.adj-recurrence').value;

      if (name && amount > 0) {
        if (recurrence === 'recurring') {
          newRecurring.push({ name, type, amount });
        } else {
          newManual.push({ name, type, amount });
        }
      }
    });

    if (emp.recurringItems) {
      const diligence = emp.recurringItems.find(item => item.name === 'เบี้ยขยัน');
      if (diligence && !newRecurring.some(i => i.name === 'เบี้ยขยัน')) {
        newRecurring.push(diligence);
      }
    }
    recurringItems = newRecurring;
    manualItems = newManual;
  }

  // Handle overrides
  const overrideSalaryInput = document.getElementById('override-salary-rate');
  let finalOverrides = oldTimesheet.overrides || null;
  if (overrideSalaryInput) {
    const overrideSSInput = document.getElementById('override-ss-rate');
    const overrideSSAmountInput = document.getElementById('override-ss-amount');
    const overrideTaxInput = document.getElementById('override-tax-rate');
    const overrideTaxAmountInput = document.getElementById('override-tax-amount');
    const overrideStudentLoanInput = document.getElementById('override-student-loan');
    const overrideSecurityDepositInput = document.getElementById('override-security-deposit');
    const overrideSecurityRefundInput = document.getElementById('override-security-refund');
    const overrideDiligenceAllowanceInput = document.getElementById('override-diligence-allowance');

    const overrides = {};
    if (overrideSalaryInput.value !== '') overrides.salaryRate = overrideSalaryInput.value;
    if (overrideSSInput && overrideSSInput.value !== '') overrides.socialSecurityRate = overrideSSInput.value;
    if (overrideSSAmountInput && overrideSSAmountInput.value !== '') overrides.socialSecurityAmount = overrideSSAmountInput.value;
    if (overrideTaxInput && overrideTaxInput.value !== '') overrides.withholdingTaxRate = overrideTaxInput.value;
    if (overrideTaxAmountInput && overrideTaxAmountInput.value !== '') overrides.withholdingTaxAmount = overrideTaxAmountInput.value;
    if (overrideStudentLoanInput && overrideStudentLoanInput.value !== '') overrides.studentLoanAmount = overrideStudentLoanInput.value;
    if (overrideSecurityDepositInput && overrideSecurityDepositInput.value !== '') overrides.securityDepositAmount = overrideSecurityDepositInput.value;
    if (overrideSecurityRefundInput && overrideSecurityRefundInput.value !== '') overrides.securityDepositRefundAmount = overrideSecurityRefundInput.value;
    if (overrideDiligenceAllowanceInput && overrideDiligenceAllowanceInput.value !== '') overrides.diligenceAllowance = overrideDiligenceAllowanceInput.value;

    finalOverrides = Object.keys(overrides).length > 0 ? overrides : null;
  }

  const dummyTimesheet = {
    ...oldTimesheet,
    days,
    manualItems,
    overrides: finalOverrides,
    isPhantom: false
  };

  // Pre-update employee in state so calculation reflects the new recurring items
  const originalRecurring = emp.recurringItems;
  emp.recurringItems = recurringItems;

  delete dummyTimesheet.summary; // Force engine to re-eval for past months
  const finalSummary = calculatePayrollForEmployee(emp, dummyTimesheet, state.activeCompanySettings);

  const updatedTimesheet = {
    ...dummyTimesheet,
    summary: finalSummary
  };

  try {
    if (canView && window.checkPermission('edit_payroll_adjustments')) {
      await updateEmployee(empId, { recurringItems });
    }
    await saveTimesheet(empId, state.selectedMonth, updatedTimesheet);

    if (!silent) toast.success("บันทึกตารางเวลาทำงานและรายการรับ/หักเรียบร้อย!");

    renderTimesheetPage();
  } catch (err) {
    emp.recurringItems = originalRecurring; // rollback on failure
    console.error(err);
    toast.error("เกิดข้อผิดพลาดในการบันทึก: " + err.message);
  }
};

async function lockTimesheetTrigger (empId) {
  const confirmed = await showConfirm({
    title: 'ยืนยันล็อกยอดเงินเดือน',
    message: 'คุณแน่ใจว่าต้องการยืนยันจ่ายเงินเดือนและล็อกประวัติเดือนนี้ถาวร? เมื่อล็อกแล้ว ข้อมูลจะไม่สามารถแก้ไขได้อีกจนกว่าจะได้รับการปลดล็อก',
    confirmText: '🔐 ยืนยันและล็อกข้อมูล',
    isDanger: false
  });
  if (!confirmed) return;

  const emp = state.employees.find(e => e.id === empId);
  const oldTimesheet = await getTimesheet(empId, state.selectedMonth);

  finalizeTimesheetDays(emp, oldTimesheet, state.selectedMonth);
  oldTimesheet.isPhantom = false;
  oldTimesheet.isLocked = true;
  delete oldTimesheet.summary; // Force engine to re-eval for past months
  const finalSummary = calculatePayrollForEmployee(emp, oldTimesheet, state.activeCompanySettings);

  const lockedTimesheet = {
    ...oldTimesheet,
    summary: finalSummary,
    isLocked: true
  };

  try {
    await saveTimesheet(empId, state.selectedMonth, lockedTimesheet);

    // Update employee profile if security deposit was deducted in this month (Requirement 5)
    if (emp.requiresSecurityDeposit && emp.securityDepositStatus === 'none') {
      const startMonth = emp.startDate ? emp.startDate.slice(0, 7) : '';
      if (state.selectedMonth === startMonth) {
        await updateEmployee(empId, { securityDepositStatus: 'deducted' });
      }
    }

    // Update employee profile if security deposit was refunded in this month (Requirement 5)
    if (emp.requiresSecurityDeposit && emp.securityDepositStatus === 'deducted' && emp.status === 'resigned' && emp.resignedDate) {
      const resignationMonth = emp.resignedDate ? emp.resignedDate.slice(0, 7) : '';
      if (state.selectedMonth === resignationMonth) {
        await updateEmployee(empId, { securityDepositStatus: 'refunded' });
      }
    }

    toast.success("🔐 ล็อกงวดการทำจ่ายของพนักงานเรียบร้อย!");
    navigateTo(state.timesheetReturnPage || 'employees');
  } catch (err) {
    console.error(err);
    toast.error("ล้มเหลวในการล็อกยอดเงินเดือน: " + err.message);
  }
};

async function unlockTimesheetTrigger (empId) {
  const confirmed = await showConfirm({
    title: 'ยืนยันการปลดล็อก',
    message: 'คุณต้องการปลดล็อกเพื่อให้แก้ไขข้อมูลเวลางานและเงินเดือนงวดเดือนนี้ใช่หรือไม่?',
    confirmText: '🔓 ปลดล็อก',
    isDanger: true
  });
  if (!confirmed) return;
  try {
    await saveTimesheet(empId, state.selectedMonth, { isLocked: false });
    toast.info("🔓 ปลดล็อกเรียบร้อยแล้ว!");
    renderTimesheetPage(); // Reopen full page
  } catch (err) {
    console.error(err);
    toast.error("เกิดข้อผิดพลาดในการปลดล็อก: " + err.message);
  }
};

// --- Quick Daily Record Auto-Fill ---
function updateQuickDailyTimes (dateStr) {
  if (!dateStr || !state.activeCompanySettings || !state.activeCompanySettings.workHoursPerDay) return;
  const emp = window.__currentQuickEmp;
  const ts = window.__currentQuickTs;

  const dateObj = new Date(dateStr);
  const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayOfWeekNames[dateObj.getDay()];
  const workHoursStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, dayName);

  let timeIn = '08:00';
  let timeOut = '17:00';

  if (workHoursStr && workHoursStr.includes('-')) {
    let cleanStr = workHoursStr;
    if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
    const [startStr, endStr] = cleanStr.split('-');
    timeIn = startStr;
    timeOut = endStr;
  } else if (workHoursStr === '0' || (workHoursStr && workHoursStr.startsWith('OFF:'))) {
    timeIn = '';
    timeOut = '';
  }

  let status = 'work';
  let advance = '';
  let overrideAmount = '';
  let lateMinutes = '';
  let remark = '';

  const d = parseInt(dateStr.split('-')[2], 10);
  const yyyyMM = dateStr.substring(0, 7);

  if (ts && ts.month === yyyyMM && ts.days && ts.days[d]) {
    const existing = ts.days[d];
    status = existing.status || 'work';

    const currentMonthStr = new Date().toISOString().slice(0, 7);
    const isPastMonth = yyyyMM < currentMonthStr;
    const isRowUntouched = ts.isPhantom && (status === 'work' || status === 'not_started') && !existing.timeIn && !existing.timeOut && !existing.advance && (existing.overrideAmount === undefined || existing.overrideAmount === null || existing.overrideAmount === '') && !existing.remark && (!existing.lateMinutes || parseInt(existing.lateMinutes) === 0);
    const now = new Date();
    const currentYear = state.selectedMonth.split('-')[0];
    const currentMonth = state.selectedMonth.split('-')[1];

    if ((!isPastMonth && isRowUntouched) || status === 'not_started') {
      const todayString = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const isSpecialWorkday = state.specialWorkdays && state.specialWorkdays.some(s => s.date === dateStr);
      const isCompensatoryHoliday = state.specialWorkdays && state.specialWorkdays.some(s => s.compensatoryOffDate === dateStr);
      const isCompanyHoliday = state.holidays && state.holidays.some(h => h.date === dateStr);

      let isDefaultDayOff = false;
      if (emp.weeklyDaysOff !== undefined && emp.weeklyDaysOff.length > 0) {
        isDefaultDayOff = emp.weeklyDaysOff.includes(dateObj.getDay());
      } else {
        const workHoursStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, dayName);
        isDefaultDayOff = workHoursStr === '0' || workHoursStr.startsWith('OFF:');
      }

      if (emp.startDate && dateStr < emp.startDate) status = 'not_started';
      else if (emp.resignDate && dateStr > emp.resignDate) status = 'resigned';
      else if (dateStr > todayString) status = 'not_started';
      else if (isSpecialWorkday) status = 'special_workday';
      else if (isCompensatoryHoliday) status = 'compensatory_holiday';
      else if (isCompanyHoliday) status = 'holiday';
      else if (isDefaultDayOff) status = 'day_off';
      else status = 'work';
    }

    if (existing.timeIn) timeIn = existing.timeIn;
    if (existing.timeOut) timeOut = existing.timeOut;
    advance = existing.advance || '';
    overrideAmount = existing.overrideAmount || '';
    lateMinutes = existing.lateMinutes || '';
    if (existing.remark) remark = existing.remark;
  }

  let overrideLabel = '💰 ยอดเงินจริง/วัน:';
  if (emp && (emp.type === 'daily' || emp.type === 'freelance')) {
    overrideLabel = `💰 ยอดเงินจริง/วัน (ปกติ: ${(emp.salaryRate || 0).toLocaleString()} บาท):`;
  }

  let advanceLabel = '💸 เบิกล่วงหน้า (บาท):';
  if (ts && ts.month === yyyyMM && emp && state.activeCompanySettings) {
    // For Quick Daily Record, we want to know max advance up to the selected date
    const maxVal = calculateMaxAdvanceAllowed(emp, ts, state.activeCompanySettings, d);
    advanceLabel = `💸 เบิกล่วงหน้า (เบิกได้สูงสุด ${Math.floor(maxVal).toLocaleString()} บาท):`;
  }

  const inEl = document.getElementById('quick-daily-in');
  const outEl = document.getElementById('quick-daily-out');
  const lateEl = document.getElementById('quick-daily-late');
  const statusEl = document.getElementById('quick-daily-status');
  const advanceEl = document.getElementById('quick-daily-advance');
  const overrideEl = document.getElementById('quick-daily-override');
  const remarkEl = document.getElementById('quick-daily-remark');
  const overrideLabelEl = document.getElementById('quick-daily-override-label');
  const advanceLabelEl = document.getElementById('quick-daily-advance-label');

  if (inEl) inEl.value = timeIn;
  if (outEl) outEl.value = timeOut;
  if (lateEl) lateEl.value = lateMinutes;
  if (statusEl) statusEl.value = status;
  if (advanceEl) advanceEl.value = advance;
  if (remarkEl) remarkEl.value = remark;

  if (overrideEl) {
    overrideEl.value = overrideAmount;
    if (emp && (emp.type === 'daily' || emp.type === 'freelance') && overrideAmount === '') {
      overrideEl.placeholder = emp.salaryRate || 0;
    } else {
      overrideEl.placeholder = 'ตามปกติ';
    }
  }
  if (overrideLabelEl) overrideLabelEl.textContent = overrideLabel;
  if (advanceLabelEl) advanceLabelEl.textContent = advanceLabel;

  if (window.calculateQuickDailyEarnings) {
    window.calculateQuickDailyEarnings();
  }
};

function calculateQuickDailyEarnings () {
  const emp = window.__currentQuickEmp;
  const ts = window.__currentQuickTs;
  if (!emp || !ts || (emp.type !== 'daily' && emp.type !== 'freelance') || !state.activeCompanySettings) return;

  const dateStr = document.getElementById('quick-daily-date').value;
  if (!dateStr) return;

  const status = document.getElementById('quick-daily-status').value;
  const timeIn = document.getElementById('quick-daily-in').value;
  const timeOut = document.getElementById('quick-daily-out').value;
  const overrideEl = document.getElementById('quick-daily-override');

  if (!overrideEl) return;

  let isPaid = false;
  if (status === 'work' || status === 'holiday_selected' || status === 'special_workday' || status === 'holiday' || status === 'compensatory_holiday') {
    isPaid = true;
  } else if (emp.type === 'daily' || emp.type === 'freelance') {
    if (state.activeCompanySettings.enableSickLeaveCalc && status === 'leave_sick') isPaid = true;
    if (state.activeCompanySettings.enableAnnualLeaveCalc && status === 'leave_annual') isPaid = true;
  }

  if (!isPaid) {
    overrideEl.placeholder = '0 (ไม่ได้รับค่าจ้าง)';
    return;
  }

  let dailyRate = ts.overrides && ts.overrides.salaryRate !== undefined ? parseFloat(ts.overrides.salaryRate) : (emp.salaryRate || 0);

  if (status === 'work') {
    const dateObj = new Date(dateStr);
    const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayOfWeekNames[dateObj.getDay()];
    const workHoursStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, dayName);

    let defaultTimeIn = '08:00';
    let defaultTimeOut = '17:00';
    let workHours = parseWorkHoursRange(workHoursStr);
    if (workHours === 0) {
      for (const fallbackDay of dayOfWeekNames) {
        const fallbackStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, fallbackDay);
        const fallbackHours = parseWorkHoursRange(fallbackStr);
        if (fallbackHours > 0) {
          workHours = fallbackHours;
          let cleanStr = fallbackStr;
          if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
          const [startStr, endStr] = cleanStr.split('-');
          defaultTimeIn = startStr;
          defaultTimeOut = endStr;
          break;
        }
      }
      if (workHours === 0) workHours = 8;
    } else if (workHoursStr && workHoursStr.includes('-')) {
      let cleanStr = workHoursStr;
      if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
      const [startStr, endStr] = cleanStr.split('-');
      defaultTimeIn = startStr;
      defaultTimeOut = endStr;
    }

    const minuteRate = dailyRate / workHours / 60;

    const lateEl = document.getElementById('quick-daily-late');
    let lateMins = lateEl && lateEl.value ? parseInt(lateEl.value, 10) : 0;
    if (isNaN(lateMins) || lateMins < 0) lateMins = 0;

    let missingMins = 0;
    if (timeIn && timeOut && defaultTimeIn && defaultTimeOut) {
      const [inH, inM] = timeIn.split(':').map(Number);
      const [startH, startM] = defaultTimeIn.split(':').map(Number);
      const [outH, outM] = timeOut.split(':').map(Number);
      const [endH, endM] = defaultTimeOut.split(':').map(Number);

      if (!isNaN(inH) && !isNaN(inM) && !isNaN(startH) && !isNaN(startM)) {
        const inTotal = inH * 60 + inM;
        const startTotal = startH * 60 + startM;
        if (inTotal > startTotal) missingMins += (inTotal - startTotal);
      }
      if (!isNaN(outH) && !isNaN(outM) && !isNaN(endH) && !isNaN(endM)) {
        const outTotal = outH * 60 + outM;
        const endTotal = endH * 60 + endM;
        if (outTotal < endTotal) missingMins += (endTotal - outTotal);
      }
    }

    const totalDeductionMins = lateMins + missingMins;
    const deduction = totalDeductionMins * minuteRate;
    let netDay = state.activeCompanySettings.partialDayRounding === 'decimal' ? Math.round((dailyRate - deduction) * 100) / 100 : Math.floor(dailyRate - deduction);
    if (netDay < 0) netDay = 0;

    if (totalDeductionMins > 0) {
      overrideEl.placeholder = `${netDay} (หัก ${totalDeductionMins} นาที)`;
    } else {
      overrideEl.placeholder = `${netDay}`;
    }
  } else {
    overrideEl.placeholder = `${dailyRate}`;
  }
};

// --- Quick Daily Record Modal ---
async function openDailyRecordModal (empId) {
  const emp = state.employees.find(e => e.id === empId);
  if (!emp) return;

  let modal = document.getElementById('quick-daily-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'quick-daily-modal';
    modal.className = 'modal-overlay';
    document.body.appendChild(modal);
  }

  const todayStr = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const ts = await getTimesheet(empId, state.selectedMonth);

  window.__currentQuickEmp = emp;
  window.__currentQuickTs = ts;

  const statusOptionsHtml = [
    `<option value="work">✅ มาทำงาน (ปกติ)</option>`,
    `<option value="absence">❌ ขาดงาน</option>`,
    `<option value="leave_personal">💼 ลา</option>`,
  ];
  if (state.activeCompanySettings.enableSickLeaveCalc) {
    statusOptionsHtml.push(`<option value="leave_sick">🤒 ลาป่วย</option>`);
  }
  if (state.activeCompanySettings.enableAnnualLeaveCalc) {
    statusOptionsHtml.push(`<option value="leave_annual">🌴 ลาพักร้อน</option>`);
  }
  statusOptionsHtml.push(
    `<option value="holiday_selected">💜 วันหยุดประจำปี (เลือกเอง)</option>`,
    `<option value="holiday">🚩 วันหยุดประจำปีบริษัท</option>`,
    `<option value="compensatory_holiday">💤 วันหยุดชดเชย</option>`,
    `<option value="special_workday">🛠️ วันทำงานพิเศษ</option>`,
    `<option value="day_off">💤 วันหยุดประจำสัปดาห์</option>`,
    `<option value="not_started">⏳ ยังไม่ถึงวัน / ยังไม่เริ่มงาน</option>`,
    `<option value="resigned">🚪 ลาออกแล้ว</option>`
  );

  modal.innerHTML = `
    <div class="modal-container" style="max-width: 500px;">
      <div class="modal-header">
        <h3>⚡ บันทึกด่วนรายวัน: ${escapeHtml(emp.realName)}</h3>
        <button class="btn-close-modal" onclick="document.getElementById('quick-daily-modal').classList.remove('active')">&times;</button>
      </div>
      <div class="modal-body">
        <div style="display: flex; flex-direction: column; gap: 15px;">
          <div>
            <label style="font-weight: 700; color: var(--primary-dark);">📅 เลือกวันที่:</label>
            <input type="date" id="quick-daily-date" value="${todayStr}" onchange="updateQuickDailyTimes(this.value)" style="width: 100%; padding: 8px; font-size: 1rem; border: 1px solid var(--gray-300); border-radius: 4px;">
          </div>
          <div>
            <label style="font-weight: 700; color: var(--primary-dark);">📋 สถานะการทำงาน:</label>
            <select id="quick-daily-status" onchange="calculateQuickDailyEarnings()" style="width: 100%; padding: 8px; font-size: 1rem; border: 1px solid var(--gray-300); border-radius: 4px;">
              ${statusOptionsHtml.join('')}
            </select>
          </div>
          <div style="display:flex; gap:10px;">
            <div style="flex:1;">
              <label style="font-weight: 700; color: var(--primary-dark);">⏰ เวลาเข้างาน:</label>
              <input type="text" id="quick-daily-in" class="time-input-24h" placeholder="08:00" oninput="calculateQuickDailyEarnings()" style="width: 100%; padding: 8px; font-size: 1rem; border: 1px solid var(--gray-300); border-radius: 4px;">
            </div>
            <div style="flex:1;">
              <label style="font-weight: 700; color: var(--primary-dark);">⏰ เวลาออกงาน:</label>
              <input type="text" id="quick-daily-out" class="time-input-24h" placeholder="17:00" oninput="calculateQuickDailyEarnings()" style="width: 100%; padding: 8px; font-size: 1rem; border: 1px solid var(--gray-300); border-radius: 4px;">
            </div>
            <div style="flex:1;">
              <label style="font-weight: 700; color: var(--danger);">⏳ มาสาย (นาที):</label>
              <input type="number" id="quick-daily-late" placeholder="0" min="0" oninput="calculateQuickDailyEarnings()" style="width: 100%; padding: 8px; font-size: 1rem; border: 1px solid var(--gray-300); border-radius: 4px;">
            </div>
          </div>
          <div>
            <label id="quick-daily-advance-label" style="font-weight: 700; color: var(--primary-dark);">💸 เบิกล่วงหน้า (บาท):</label>
            <input type="number" id="quick-daily-advance" placeholder="0" min="0" style="width: 100%; padding: 8px; font-size: 1rem; border: 1px solid var(--gray-300); border-radius: 4px;">
          </div>
          <div>
            <label id="quick-daily-override-label" style="font-weight: 700; color: var(--primary-dark);">💰 ยอดเงินจริง/วัน:</label>
            <input type="number" id="quick-daily-override" placeholder="ตามปกติ" min="0" style="width: 100%; padding: 8px; font-size: 1rem; border: 1px solid var(--gray-300); border-radius: 4px;">
          </div>
          <div>
            <label style="font-weight: 700; color: var(--primary-dark);">📝 หมายเหตุ:</label>
            <input type="text" id="quick-daily-remark" placeholder="หมายเหตุ (ถ้ามี)" style="width: 100%; padding: 8px; font-size: 1rem; border: 1px solid var(--gray-300); border-radius: 4px;">
          </div>
        </div>
      </div>
      <div class="modal-footer" style="padding: 15px 20px; border-top: 1px solid var(--gray-300); display: flex; justify-content: flex-end; gap: 10px;">
        <button class="btn btn-secondary" onclick="document.getElementById('quick-daily-modal').classList.remove('active')">ยกเลิก</button>
        <button class="btn btn-primary" onclick="saveQuickDailyRecordTrigger('${escapeJsParam(empId)}')">💾 บันทึกข้อมูล</button>
      </div>
    </div>
  `;

  modal.classList.add('active');
  window.updateQuickDailyTimes(todayStr);
};

async function saveQuickDailyRecordTrigger (empId) {
  const dateStr = document.getElementById('quick-daily-date').value;
  if (!dateStr) {
    alert("กรุณาเลือกวันที่");
    return;
  }

  const [yearStr, monthStr, dayStr] = dateStr.split('-');
  const yyyyMM = `${yearStr}-${monthStr}`;
  const d = parseInt(dayStr, 10);

  if (yyyyMM !== state.selectedMonth) {
    alert(`ระบบกำลังแสดงงวดเดือน ${state.selectedMonth} แต่คุณเลือกวันที่ของเดือน ${yyyyMM} กรุณาเปลี่ยนงวดเดือนหลักก่อน`);
    return;
  }

  const ts = await getTimesheet(empId, state.selectedMonth);
  if (ts.isLocked) {
    alert("ไม่สามารถบันทึกได้เนื่อจากงวดนี้ถูกล็อคแล้ว");
    return;
  }

  const status = document.getElementById('quick-daily-status').value;
  const timeIn = document.getElementById('quick-daily-in').value;
  const timeOut = document.getElementById('quick-daily-out').value;
  const advance = parseFloat(document.getElementById('quick-daily-advance').value || 0);
  const overrideInput = document.getElementById('quick-daily-override').value;
  let overrideAmount = overrideInput !== '' ? parseFloat(overrideInput) : null;
  const remark = document.getElementById('quick-daily-remark').value.trim();

  const emp = state.employees.find(e => e.id === empId);

  if (advance > 0 && emp && state.activeCompanySettings) {
    const maxVal = calculateMaxAdvanceAllowed(emp, ts, state.activeCompanySettings, d);
    if (advance > maxVal) {
      const excess = advance - maxVal;
      const confirmed = await showConfirm({
        title: '⚠️ แจ้งเตือนการเบิกเงินเกินรายได้',
        message: `ยอดเงินที่จะเบิกในวันที่ ${d} (${advance.toLocaleString()} บาท) เกินวงเงินรายได้สะสมที่เบิกได้ ณ วันนั้น (${maxVal.toLocaleString()} บาท)\n\n• กำลังเบิกเกิน: ${excess.toLocaleString()} บาท\n\nคุณยังคงยืนยันที่จะเบิกเงินจำนวนนี้หรือไม่? หรือต้องการยกเลิกไม่เบิก?`,
        confirmText: '✅ ยืนยันที่จะเบิก',
        cancelText: '❌ ยกเลิกไม่เบิก',
        isDanger: true,
        icon: '💸'
      });
      if (!confirmed) {
        return;
      }
    }
  }

  // Auto-fill overrideAmount for partial work time, mirroring saveTimesheetEditing logic
  if ((emp.type === 'daily' || emp.type === 'freelance') && (status === 'work' || status === 'holiday_selected' || status === 'special_workday' || status === 'holiday') && timeIn && timeOut) {
    const dailyRate = ts.overrides && ts.overrides.salaryRate !== undefined ? parseFloat(ts.overrides.salaryRate) : (emp.salaryRate || 0);
    const dateObj = new Date(dateStr);
    const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayOfWeekNames[dateObj.getDay()];
    const workHoursStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, dayName);

    let defaultTimeIn = '';
    let defaultTimeOut = '';
    let workHours = parseWorkHoursRange(workHoursStr);

    if (workHours === 0) {
      for (const fallbackDay of dayOfWeekNames) {
        const fallbackStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, fallbackDay);
        const fallbackHours = parseWorkHoursRange(fallbackStr);
        if (fallbackHours > 0) {
          let cleanStr = fallbackStr;
          if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
          const [startStr, endStr] = cleanStr.split('-');
          defaultTimeIn = startStr;
          defaultTimeOut = endStr;
          break;
        }
      }
    } else if (workHoursStr && workHoursStr.includes('-')) {
      let cleanStr = workHoursStr;
      if (cleanStr.startsWith('OFF:')) cleanStr = cleanStr.substring(4);
      const [startStr, endStr] = cleanStr.split('-');
      defaultTimeIn = startStr;
      defaultTimeOut = endStr;
    }

    if (defaultTimeIn && defaultTimeOut) {
      const [inH, inM] = timeIn.split(':').map(Number);
      const [startH, startM] = defaultTimeIn.split(':').map(Number);
      const [outH, outM] = timeOut.split(':').map(Number);
      const [endH, endM] = defaultTimeOut.split(':').map(Number);

      let missingMins = 0;
      if (!isNaN(inH) && !isNaN(inM) && !isNaN(startH) && !isNaN(startM)) {
        const inTotal = inH * 60 + inM;
        const startTotal = startH * 60 + startM;
        if (inTotal > startTotal) missingMins += (inTotal - startTotal);
      }
      if (!isNaN(outH) && !isNaN(outM) && !isNaN(endH) && !isNaN(endM)) {
        const outTotal = outH * 60 + outM;
        const endTotal = endH * 60 + endM;
        if (outTotal < endTotal) missingMins += (endTotal - outTotal);
      }

      if (missingMins > 0 && overrideAmount === null) {
        const calcWorkHours = (endH * 60 + endM - (startH * 60 + startM)) / 60;
        if (calcWorkHours > 0) {
          const minuteRate = dailyRate / calcWorkHours / 60;
          let netDaily = dailyRate - (missingMins * minuteRate);
          if (netDaily < 0) netDaily = 0;
          if (state.activeCompanySettings.partialDayRounding === 'decimal') {
            overrideAmount = Math.round(netDaily * 100) / 100;
          } else {
            overrideAmount = Math.floor(netDaily);
          }
        }
      }
    }
  }

  const lateInput = document.getElementById('quick-daily-late').value;
  const lateMinutes = lateInput !== '' ? parseInt(lateInput, 10) : 0;

  const days = ts.days || {};
  days[d] = { status, lateMinutes, earlyLeaveMinutes: 0, advance, timeIn, timeOut, overrideAmount, remark };

  const dummyTimesheet = { ...ts, days, isPhantom: false };
  const finalSummary = calculatePayrollForEmployee(emp, dummyTimesheet, state.activeCompanySettings);

  const updatedTimesheet = { ...dummyTimesheet, summary: finalSummary };

  try {
    await saveTimesheet(empId, state.selectedMonth, updatedTimesheet);
    toast.success("⚡ บันทึกข้อมูลรายวันเรียบร้อยแล้ว!");
    document.getElementById('quick-daily-modal').classList.remove('active');
  } catch (err) {
    console.error(err);
    toast.error("เกิดข้อผิดพลาดในการบันทึก: " + err.message);
  }
};

// Global 24-hour time mask listener (Supports '.' to ':')
if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  document.addEventListener('input', function (e) {
  if (e.target && e.target.classList && e.target.classList.contains('time-input-24h')) {
    let val = e.target.value.replace(/\./g, ':');
    val = val.replace(/[^0-9:]/g, '');
    if (val.length === 2 && !val.includes(':') && e.data !== null && e.inputType !== 'deleteContentBackward') {
      val += ':';
    }
    if (val.length > 5) val = val.substring(0, 5);
    e.target.value = val;
  }
});
}


// Bind all attendance & timesheet functions to window for backwards compatibility
if (typeof window !== 'undefined') {
  window.renderAttendancePage = renderAttendancePage;
  window.openEmployeeTimesheetPage = openEmployeeTimesheetPage;
  window.goBackFromTimesheet = goBackFromTimesheet;
  window.initRetroactiveTimesheet = initRetroactiveTimesheet;
  window.renderTimesheetPage = renderTimesheetPage;
  window.onGridStatusChange = onGridStatusChange;
  window.recalculateGridTotals = recalculateGridTotals;
  window.validateDayAdvance = validateDayAdvance;
  window.saveTimesheetTrigger = saveTimesheetTrigger;
  window.lockTimesheetTrigger = lockTimesheetTrigger;
  window.unlockTimesheetTrigger = unlockTimesheetTrigger;
  window.updateQuickDailyTimes = updateQuickDailyTimes;
  window.calculateQuickDailyEarnings = calculateQuickDailyEarnings;
  window.openDailyRecordModal = openDailyRecordModal;
  window.saveQuickDailyRecordTrigger = saveQuickDailyRecordTrigger;
}

export {
  renderAttendancePage,
  openEmployeeTimesheetPage,
  goBackFromTimesheet,
  initRetroactiveTimesheet,
  renderTimesheetPage,
  onGridStatusChange,
  recalculateGridTotals,
  validateDayAdvance,
  saveTimesheetTrigger,
  lockTimesheetTrigger,
  unlockTimesheetTrigger,
  updateQuickDailyTimes,
  calculateQuickDailyEarnings,
  openDailyRecordModal,
  saveQuickDailyRecordTrigger
};
